import React, { useEffect, useState } from "react";
import { adsGetVisualAttrs, adsReExtractVisualAttrs } from "../../services/api";
import { OriginTag } from "./FieldPicker";

// E4.4 - renders a creative's focus-point JSON (visual_attributes_json) as
// labeled cards inside the matrix creative-detail view. Lazy-loads on expand.

function Chips({ items, tone = "#eef2ff", color = "#3730a3" }) {
  if (!items || !items.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {items.map((t, i) => (
        <span key={i} style={{ background: tone, color, borderRadius: 10, padding: "1px 8px", fontSize: 11, fontWeight: 700 }}>{t}</span>
      ))}
    </div>
  );
}

function Swatches({ colors }) {
  if (!colors || !colors.length) return null;
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {colors.slice(0, 6).map((c, i) => (
        <span key={i} title={c} style={{ width: 18, height: 18, borderRadius: 4, background: c, border: "1px solid #e5e7eb", display: "inline-block" }} />
      ))}
    </div>
  );
}

function Row({ label, children }) {
  if (children == null || children === "" ) return null;
  return (
    <div style={{ display: "flex", gap: 8, padding: "3px 0" }}>
      <div style={{ minWidth: 110, fontSize: 11, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.03em" }}>{label}</div>
      <div style={{ fontSize: 12.5, color: "#111827", flex: 1 }}>{children}</div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div style={{ background: "#fafafe", border: "1px solid #eceef5", borderRadius: 8, padding: "8px 10px" }}>
      <div style={{ fontSize: 11, fontWeight: 900, color: "#4f46e5", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>{title}</div>
      {children}
    </div>
  );
}

export default function VisualAttributesPanel({ creativeId, isAdmin = false }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchAttrs = async () => {
    setLoading(true); setErr(null);
    try { setData(await adsGetVisualAttrs(creativeId)); }
    catch (e) { setErr(e.message || "Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (open && data === null && !loading) fetchAttrs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onReExtract = async () => {
    setBusy(true); setErr(null);
    try {
      await adsReExtractVisualAttrs(creativeId);
      // re-extract may be queued (async) - re-poll shortly
      setTimeout(fetchAttrs, 1500);
    } catch (e) { setErr(e.message || "Re-extract failed"); }
    finally { setBusy(false); }
  };

  const va = data?.attributes;

  return (
    <div style={{ marginTop: 8 }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{ background: "none", border: "1px solid #e5e7eb", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 800, color: "#4f46e5", cursor: "pointer" }}
      >
        {open ? "▾" : "▸"} Visual attributes{data?.stale ? " ⚠ stale" : ""}
        <OriginTag origin="ai" />
      </button>

      {open && (
        <div style={{ marginTop: 8 }}>
          {loading && <div style={{ fontSize: 12, color: "#6b7280" }}>Loading…</div>}
          {err && <div style={{ fontSize: 12, color: "#b91c1c" }}>{err}</div>}
          {!loading && data && !va && (
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Not extracted yet.{" "}
              {isAdmin && <button onClick={onReExtract} disabled={busy} style={extractBtn}>{busy ? "Queuing…" : "Extract now"}</button>}
            </div>
          )}
          {va && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <Card title="Scene">
                  <Row label="Setting">{va.scene?.setting}</Row>
                  <Row label="Environment">{va.scene?.environment}</Row>
                  <Row label="Time">{va.scene?.time_of_day}</Row>
                </Card>
                <Card title="People">
                  <Row label="Total">{va.people?.total_count}</Row>
                  <Row label="Gender">
                    {va.people ? `W ${va.people.by_apparent_gender?.women ?? 0} · M ${va.people.by_apparent_gender?.men ?? 0}` : null}
                  </Row>
                  <Row label="Expression">{va.people?.dominant_expression}</Row>
                </Card>
                <Card title="Subject">
                  <Row label="Primary">{va.subject?.primary}</Row>
                  <Row label="Product">{va.subject?.product_category_guess}</Row>
                </Card>
                <Card title="Composition">
                  <Row label="Palette">{va.composition?.color_palette}</Row>
                  <Row label="Colors"><Swatches colors={va.composition?.dominant_colors} /></Row>
                  <Row label="Busyness">{va.composition?.busyness}</Row>
                </Card>
                <Card title="Brand">
                  <Row label="Visible">{String(va.brand_identity?.brand_visible ?? "")}</Row>
                  <Row label="Logo">{va.brand_identity?.logo_position}</Row>
                </Card>
                <Card title="On-image text">
                  <Row label="Headline">{va.text_overlay?.headline_verbatim}</Row>
                  <Row label="CTA">{va.text_overlay?.cta_on_image}</Row>
                  <Row label="Discount">{String(va.text_overlay?.discount_visible ?? "")}</Row>
                </Card>
                <Card title="Marketing">
                  <Row label="Funnel">{va.marketing_interpretation?.funnel_stage}</Row>
                  <Row label="Intent">{va.marketing_interpretation?.intent}</Row>
                  <Row label="Tone">{va.marketing_interpretation?.tone}</Row>
                  <Row label="Pattern">{va.marketing_interpretation?.persuasion_pattern}</Row>
                  <Row label="Claims"><Chips items={va.marketing_interpretation?.claim_types} /></Row>
                </Card>
                <Card title="Format">
                  <Row label="Type">{va.format?.creative_type}</Row>
                  <Row label="Quality">{va.format?.perceived_quality}</Row>
                  <Row label="Style">{va.format?.photography_style}</Row>
                </Card>
              </div>
              {va.description && (
                <div style={{ marginTop: 8, fontSize: 12.5, color: "#374151", fontStyle: "italic", background: "#fafafe", border: "1px solid #eceef5", borderRadius: 8, padding: "8px 10px" }}>
                  {va.description}
                </div>
              )}
              <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 10, fontSize: 11, color: "#9ca3af" }}>
                <span>{data.model} · schema v{data.schema_version}</span>
                {data.cost_usd != null && <span>${Number(data.cost_usd).toFixed(4)}</span>}
                {isAdmin && (
                  <button onClick={onReExtract} disabled={busy} style={extractBtn}>{busy ? "Queuing…" : "Re-extract"}</button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const extractBtn = { background: "none", border: "1px solid #c7d2fe", borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 800, color: "#4f46e5", cursor: "pointer" };
